import { 
  CreateExpenseRequest, 
  UpdateExpenseRequest, 
  DeleteExpenseRequest,
  ExpenseWithCategory,
  RecentExpensesResponse,
  ExpenseFilters,
  ExpenseSort 
} from '../../types/expense';

/**
 * API client for expense operations
 */
export class ExpenseAPI {
  private static baseUrl = '/.netlify/functions';

  /**
   * Create a new expense
   */
  static async createExpense(data: CreateExpenseRequest): Promise<ExpenseWithCategory> {
    const response = await fetch(`${this.baseUrl}/addExpense`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to create expense');
    }

    return result.data;
  }

  /**
   * Get recent expenses with optional filters and pagination
   */
  static async getRecentExpenses(params: {
    couple_id: string;
    page?: number;
    limit?: number;
    filters?: ExpenseFilters;
    sort?: ExpenseSort;
  }): Promise<RecentExpensesResponse> {
    const queryParams = new URLSearchParams({
      couple_id: params.couple_id,
      page: (params.page || 1).toString(),
      limit: (params.limit || 20).toString(),
    });

    // Add filters to query params
    if (params.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    // Add sort to query params
    if (params.sort) {
      queryParams.append('sort_field', params.sort.field);
      queryParams.append('sort_direction', params.sort.direction);
    }

    const response = await fetch(`${this.baseUrl}/getRecentExpenses?${queryParams}`);

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to fetch expenses');
    }

    return result.data;
  }

  /**
   * Update an existing expense
   */
  static async updateExpense(data: UpdateExpenseRequest): Promise<ExpenseWithCategory> {
    const response = await fetch(`${this.baseUrl}/updateExpense`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to update expense');
    }

    return result.data;
  }

  /**
   * Delete an expense
   */
  static async deleteExpense(data: DeleteExpenseRequest): Promise<void> {
    const response = await fetch(`${this.baseUrl}/deleteExpense`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to delete expense');
    }
  }
}

/**
 * Helper functions for working with expense data
 */
export const ExpenseHelpers = {
  /**
   * Calculate expense statistics from a list of expenses
   */
  calculateStats: (expenses: ExpenseWithCategory[]) => {
    const stats = {
      total_amount: 0,
      expense_count: expenses.length,
      average_amount: 0,
      partner1_total: 0,
      partner2_total: 0,
      partner1_count: 0,
      partner2_count: 0,
      top_category: null as { name: string; amount: number; count: number } | null,
    };

    const categoryTotals = new Map<string, { amount: number; count: number }>();

    expenses.forEach(expense => {
      const amount = Number(expense.amount);
      stats.total_amount += amount;

      if (expense.added_by === 'partner1') {
        stats.partner1_total += amount;
        stats.partner1_count += 1;
      } else {
        stats.partner2_total += amount;
        stats.partner2_count += 1;
      }

      // Track category totals
      const categoryKey = expense.category_name;
      const current = categoryTotals.get(categoryKey) || { amount: 0, count: 0 };
      categoryTotals.set(categoryKey, {
        amount: current.amount + amount,
        count: current.count + 1,
      });
    });

    // Calculate average
    stats.average_amount = stats.expense_count > 0 ? stats.total_amount / stats.expense_count : 0;

    // Find top category
    let topCategory = { name: '', amount: 0, count: 0 };
    categoryTotals.forEach((totals, name) => {
      if (totals.amount > topCategory.amount) {
        topCategory = { name, amount: totals.amount, count: totals.count };
      }
    });

    if (topCategory.name) {
      stats.top_category = topCategory;
    }

    return stats;
  },

  /**
   * Group expenses by month
   */
  groupByMonth: (expenses: ExpenseWithCategory[]) => {
    const monthlyData = new Map<string, {
      month: string;
      year: number;
      total_amount: number;
      partner1_amount: number;
      partner2_amount: number;
      expense_count: number;
      expenses: ExpenseWithCategory[];
    }>();

    expenses.forEach(expense => {
      const expenseDate = new Date(expense.expense_date);
      const monthKey = `${expenseDate.getFullYear()}-${String(expenseDate.getMonth() + 1).padStart(2, '0')}`;
      
      const existing = monthlyData.get(monthKey) || {
        month: expenseDate.toLocaleDateString('en-US', { month: 'long' }),
        year: expenseDate.getFullYear(),
        total_amount: 0,
        partner1_amount: 0,
        partner2_amount: 0,
        expense_count: 0,
        expenses: [],
      };

      const amount = Number(expense.amount);
      existing.total_amount += amount;
      existing.expense_count += 1;
      existing.expenses.push(expense);

      if (expense.added_by === 'partner1') {
        existing.partner1_amount += amount;
      } else {
        existing.partner2_amount += amount;
      }

      monthlyData.set(monthKey, existing);
    });

    return Array.from(monthlyData.values()).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return new Date(`${a.month} 1, ${a.year}`).getMonth() - new Date(`${b.month} 1, ${b.year}`).getMonth();
    });
  },

  /**
   * Filter expenses by date range
   */
  filterByDateRange: (expenses: ExpenseWithCategory[], startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return expenses.filter(expense => {
      const expenseDate = new Date(expense.expense_date);
      return expenseDate >= start && expenseDate <= end;
    });
  },

  /**
   * Sort expenses by field and direction
   */
  sortExpenses: (expenses: ExpenseWithCategory[], sort: ExpenseSort) => {
    return [...expenses].sort((a, b) => {
      let aValue: any = a[sort.field];
      let bValue: any = b[sort.field];

      // Handle special cases
      if (sort.field === 'amount') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      } else if (sort.field.includes('date')) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Sort comparison
      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;

      return sort.direction === 'desc' ? -comparison : comparison;
    });
  },
};